import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import type { Action } from "@/components/MenuButton";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import { useCanViewPartialBackgroundCheck } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import usePageTitle from "@/hooks/usePageTitle";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { Navigate, useParams } from "@/router";
import { GET_PROSPECT_PROGRAM_QUERY } from "@/pages/people/[pgaId]/person/queries";
import BackgroundCheckOrdersPanel from "./BackgroundCheckOrdersPanel";
import { backgroundCheckFocusedPagePath } from "./backgroundCheckPaths";
import { canViewBackgroundCheckStaffSection } from "./backgroundCheckEligibility";
import { EDIT_BACKGROUND_CHECK_LABEL, FOCUSED_PAGE_LOAD_ERROR, NEW_ORDER_LABEL } from "./backgroundCheckCopy";
import type { BackgroundCheckFocusedPageActions } from "./BackgroundCheckFocusedPageActions";

export default function BackgroundCheckFocusedPage() {
  const { pgaId = "" } = useParams("/people/:pgaId/membership/path-to-membership/background-check");
  const flags = useFlags();
  const canViewBackgroundCheck = useCanViewPartialBackgroundCheck();
  const staffSectionVisible = canViewBackgroundCheckStaffSection({
    flags,
    canViewPartialBackgroundCheck: canViewBackgroundCheck
  });
  const [focusedPageActions, setFocusedPageActions] = useState<BackgroundCheckFocusedPageActions | null>(null);

  usePageTitle("Background Checks");

  const { data, loading, error } = useQuery(GET_PROSPECT_PROGRAM_QUERY, {
    variables: { pgaId },
    skip: !pgaId,
    fetchPolicy: "cache-first"
  });

  const person = data?.person;

  const clusterActions = useMemo((): Action[] => {
    if (!focusedPageActions) return [];

    const actions: Action[] = [];

    if (focusedPageActions.canEditDisposition) {
      actions.push({
        text: EDIT_BACKGROUND_CHECK_LABEL,
        variant: "outlined",
        onClick: focusedPageActions.openEditDisposition,
        testId: "edit-background-check-action"
      });
    }

    if (focusedPageActions.createEnabled) {
      actions.push({
        text: NEW_ORDER_LABEL,
        menuOnly: true,
        iconName: "add",
        onClick: focusedPageActions.openCreateOrder,
        testId: "create-background-check-order-button"
      });
    }

    return actions;
  }, [focusedPageActions]);

  const backLink = {
    to: "/people/:pgaId/membership/path-to-membership" as const,
    params: { pgaId },
    title: "Back"
  };

  if (!staffSectionVisible) {
    return <Navigate to="/people/:pgaId/membership/path-to-membership" params={{ pgaId }} replace />;
  }

  if (error) {
    return (
      <FocusedPageLayout backLink={backLink}>
        <PersonObjectHeading pgaId={pgaId} hideActions disableSticky />
        <p className="pt-12 text-body-medium text-sys-brand-on-surface-variant">{FOCUSED_PAGE_LOAD_ERROR}</p>
      </FocusedPageLayout>
    );
  }

  if (loading || !person) {
    return (
      <FocusedPageLayout backLink={backLink}>
        <PersonObjectHeading pgaId={pgaId} hideActions disableSticky />
      </FocusedPageLayout>
    );
  }

  return (
    <FocusedPageLayout backLink={backLink} clusterActions={clusterActions} stickyActions>
      <PersonObjectHeading pgaId={pgaId} hideActions disableSticky />
      <BackgroundCheckOrdersPanel
        layout="focused"
        personId={person.id}
        pgaId={pgaId}
        backgroundCheck={person.prospectProgram?.backgroundCheck}
        onFocusedPageActionsChange={setFocusedPageActions}
      />
    </FocusedPageLayout>
  );
}

export { backgroundCheckFocusedPagePath };
